import Nanotoast from "../components/NanoToast";
import Bugsnag from "@bugsnag/js";
import { ApiError, ForbiddenError, NotFoundError, ServerError } from "../Error/ApiErrors";

/**
 * A utility class with a collection of methods surrounding REST API calls
 * and the functions we commonly use to deal with those.
 */
class RestPromise {
  /**
   * This is a sort of factory method for an Ajax request
   * @param url
   * @param method
   * @param content
   * @param notifyActivity
   * @returns Promise
   */
  public static async Create<T = any>(
    url: string,
    method: "GET" | "PUT" | "POST" | "DELETE",
    content: object = null,
    notifyActivity = true,
  ): Promise<T> {
    if (notifyActivity) {
      $(window).trigger("user-activity");
    }

    const options: JQueryAjaxSettings = {
      type: method,
      url: url,
      contentType: "application/json",
      dataType: "json",
    };
    if (content !== null) {
      options.data = JSON.stringify(content);
    }

    try {
      return await $.ajax(options);
    } catch (error) {
      return this.handleApiError<T>(error);
    }
  }

  public static DisplayRestSuccess(message: string): void {
    const successToast = new Nanotoast({
      parentContainer: "body",
      pos: "nanotoast-top-center",
      type: "success",
      text: message,
      asideIcon: "far fa-thumbs-up",
      duration: 300,
      hideDelay: 5000,
    });
    successToast.Show();
  }

  public static DisplayRestError(error: ApiError): void {
    if (+error.status !== 500 && +error.status !== 0) {
      const errorToast = new Nanotoast({
        parentContainer: "body",
        pos: "nanotoast-top-center",
        type: "error",
        text: error.message,
        asideIcon: "fas fa-bug",
        duration: 300,
        hideDelay: 5000,
        zIndex: 350,
      });
      errorToast.Show();
    } else {
      Bugsnag.notify(error);
    }
  }

  public static DisplayRestMessage(
    message: string,
    className: string,
    icon = "fa-bug",
  ) {
    const errorToast = new Nanotoast({
      parentContainer: "body",
      pos: "nanotoast-top-center",
      type: className,
      text: message,
      asideIcon: `fas ${icon}`,
      duration: 300,
      hideDelay: 5000,
      zIndex: 350,
    });
    errorToast.Show();
  }

  public static DisplayRestWarning(message: string) {
    const errorToast = new Nanotoast({
      parentContainer: "body",
      pos: "nanotoast-top-center",
      type: "warning",
      text: message,
      asideIcon: "fas fa-exclamation",
      duration: 300,
      hideDelay: 5000,
      zIndex: 350,
    });
    errorToast.Show();
  }

  /**
   * Ping the server, returns true if the server answer a 200 response
   * @constructor
   */
  public static RequestServerHealth(): Promise<boolean> {
    return new Promise((resolve) => {
      const xhr = new XMLHttpRequest();
      xhr.open("GET", "/health", true);
      xhr.setRequestHeader("Content-Type", "application/json");
      xhr.onload = () => {
        resolve(xhr.status === 200);
      };
      xhr.timeout = 2000;
      xhr.ontimeout = () => resolve(false);
      xhr.onerror = () => resolve(false);
      xhr.send();
    });
  }

  /**
   * Handles API errors from jQuery AJAX calls and converts them to typed errors
   * @param error The error caught from jQuery ajax
   * @returns Promise that either resolves (for redirects) or rejects with a typed error
   */
  private static async handleApiError<T>(error: unknown): Promise<T> {
    // Define type for jQuery ajax errors
    interface AjaxError {
      status: number;
      responseText: string;
      responseJSON?: {
        message?: string;
        detail?: string;
      };
    }

    // Cast to our interface
    const ajaxError = error as AjaxError;

    // Extract error message
    let errorMessage = "Unknown error occurred";

    if (ajaxError.responseJSON?.message) {
      errorMessage = ajaxError.responseJSON.message;
    } else if (ajaxError.responseJSON?.detail) {
      errorMessage = ajaxError.responseJSON.detail;
    } else if (ajaxError.responseText) {
      try {
        // Try to parse responseText as JSON
        const parsedError = JSON.parse(ajaxError.responseText);
        errorMessage =
          parsedError.message || parsedError.detail || ajaxError.responseText;
      } catch {
        // If parsing fails, use responseText directly
        errorMessage = ajaxError.responseText;
      }
    }

    // Handle errors based on status code
    switch (ajaxError.status) {
      case 401: {
        if (errorMessage.includes("login_token_invalid")) {
          window.location.href = "/logout?logout-reason=second-login";
          return;
        } else {
          window.location.href = "/logout?logout-reason=logged-out";
          return;
        }
      }

      case 403:
        throw new ForbiddenError(errorMessage, ajaxError.responseJSON);

      case 404:
        throw new NotFoundError(errorMessage, ajaxError.responseJSON);

      case 500:
        throw new ServerError(errorMessage, ajaxError.responseJSON);

      default:
        // For all other errors, throw a generic ApiError
        throw new ApiError(
          errorMessage,
          ajaxError.status || 0,
          ajaxError.responseJSON,
        );
    }
  }
}

export default RestPromise;
